import {
  Button,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  Switch,
  TextInput,
  Tooltip
} from '@patternfly/react-core';
import { CheckIcon, PencilAltIcon, TimesIcon } from '@patternfly/react-icons';
import * as React from 'react';

import {
  PARAMETER_TYPE,
  PARAMETER_TYPE_HINTS,
  Parameter,
  parameterListValues,
  validateParameterValue
} from '@app/model/Parameter';

export interface ParameterValueEditorProps {
  parameter: Parameter;
  /** A write for this parameter is in flight; the widget locks rather than queueing a second. */
  isSaving?: boolean;
  /** Hides every way into edit mode, for a user who may read the page but not change it. */
  isReadOnly?: boolean;
  /** Given the value as text, whatever the type — that is how the endpoint takes it. */
  onSave(value: string): void | Promise<void>;
}

/**
 * The value cell of one parameter, with the widget chosen from the declared type rather than
 * from anything hardcoded per setting. That is what keeps this page dynamic: a parameter added
 * to the service's catalog shows up here, editable, with no change on this side.
 *
 * BOOLEAN is deliberately the odd one out. It is a switch that writes as soon as it is flipped,
 * because a two-state setting has nothing to type and asking for a confirming click on top of
 * the flip would be one click too many. Every other type buffers what is typed and writes on
 * Save, so a half-typed duration is never sent.
 */
export const ParameterValueEditor: React.FunctionComponent<ParameterValueEditorProps> = ({
  parameter,
  isSaving = false,
  isReadOnly = false,
  onSave
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(parameter.value ?? '');

  // A value can change under the page — another admin's write arrives on the next refresh, and a
  // reset replaces it outright. Re-seed the buffer from the parameter unless the user is typing
  // into it, which would otherwise throw their edit away mid-keystroke.
  React.useEffect(() => {
    if (!isEditing) {
      setDraft(parameter.value ?? '');
    }
  }, [parameter.value, isEditing]);

  const validationError = validateParameterValue(parameter.type, draft);

  const startEditing = () => {
    setDraft(parameter.value ?? '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(parameter.value ?? '');
    setIsEditing(false);
  };

  const commit = async () => {
    if (validationError) return;

    const next = draft.trim();
    // Nothing to write. Worth catching here rather than sending it: a no-op PUT still writes an
    // audit revision, and history is what an operator reads to find out what actually changed.
    if (next === (parameter.value ?? '').trim()) {
      setIsEditing(false);
      return;
    }

    await onSave(next);
    setIsEditing(false);
  };

  if (parameter.type === PARAMETER_TYPE.BOOLEAN) {
    const isChecked = (parameter.value ?? '').trim().toLowerCase() === 'true';
    return (
      <Switch
        id={`parameter-switch-${parameter.name}`}
        aria-label={`${parameter.property} enabled`}
        label={isChecked ? 'true' : 'false'}
        isChecked={isChecked}
        isDisabled={isSaving || isReadOnly}
        onChange={(_event, checked) => onSave(checked ? 'true' : 'false')}
      />
    );
  }

  if (isEditing) {
    return (
      <div>
        <Flex
          alignItems={{ default: 'alignItemsCenter' }}
          spaceItems={{ default: 'spaceItemsSm' }}
          flexWrap={{ default: 'nowrap' }}
        >
          <FlexItem grow={{ default: 'grow' }}>
            <TextInput
              id={`parameter-input-${parameter.name}`}
              aria-label={`${parameter.property} value`}
              value={draft}
              type={parameter.type === PARAMETER_TYPE.INTEGER ? 'number' : 'text'}
              validated={validationError ? 'error' : 'default'}
              isDisabled={isSaving}
              onChange={(_event, value) => setDraft(value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void commit();
                }
                if (event.key === 'Escape') {
                  cancelEditing();
                }
              }}
            />
          </FlexItem>
          <FlexItem>
            <Tooltip content="Save">
              <Button
                variant="link"
                aria-label={`Save ${parameter.property}`}
                icon={<CheckIcon />}
                isDisabled={!!validationError || isSaving}
                onClick={() => void commit()}
              />
            </Tooltip>
          </FlexItem>
          <FlexItem>
            <Tooltip content="Cancel">
              <Button
                variant="link"
                aria-label={`Cancel editing ${parameter.property}`}
                icon={<TimesIcon />}
                isDisabled={isSaving}
                onClick={cancelEditing}
              />
            </Tooltip>
          </FlexItem>
        </Flex>
        <HelperText>
          <HelperTextItem variant={validationError ? 'error' : 'indeterminate'}>
            {validationError ?? PARAMETER_TYPE_HINTS[parameter.type]}
          </HelperTextItem>
        </HelperText>
      </div>
    );
  }

  return (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsSm' }}
      flexWrap={{ default: 'nowrap' }}
    >
      <FlexItem>
        <ParameterValueDisplay parameter={parameter} />
      </FlexItem>
      {!isReadOnly && (
        <FlexItem>
          <Tooltip content="Edit value">
            <Button
              variant="plain"
              aria-label={`Edit ${parameter.property}`}
              icon={<PencilAltIcon />}
              isDisabled={isSaving}
              onClick={startEditing}
            />
          </Tooltip>
        </FlexItem>
      )}
    </Flex>
  );
};

/**
 * A value as it reads rather than as it is stored. A list is shown as the entries the service
 * will hold, so a stray comma is visible instead of hiding inside one string, and an empty
 * string says so — otherwise a cleared setting looks identical to a rendering bug.
 */
export const ParameterValueDisplay: React.FunctionComponent<{ parameter: Parameter }> = ({ parameter }) => {
  const { type, value } = parameter;

  if (type === PARAMETER_TYPE.STRING_LIST) {
    const entries = parameterListValues(value);
    if (entries.length === 0) return <EmptyValue />;
    return (
      <LabelGroup numLabels={6}>
        {entries.map(entry => (
          <Label key={entry} isCompact color="blue">{entry}</Label>
        ))}
      </LabelGroup>
    );
  }

  if (!value || value.trim().length === 0) return <EmptyValue />;

  return (
    <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)', wordBreak: 'break-word' }}>
      {value}
    </span>
  );
};

const EmptyValue: React.FunctionComponent = () => (
  <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>empty</span>
);
